import { EntityType } from '@/components/entities/entityType';
import { ExecutionStep, SubagentInfo } from '@/interfaces/pipelineExecution.interface';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorIcon from '@mui/icons-material/Error';
import ExtensionIcon from '@mui/icons-material/Extension';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import AppsIcon from '@mui/icons-material/Apps';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HubIcon from '@mui/icons-material/Hub';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import {
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Chip,
  alpha,
} from '@mui/material';
import React, { useState } from 'react';

type Props = {
  pipelineName?: string;
  pipelineStatus?: string;
  steps: any[];
  selectedStep: any;
  selectedView?: string;
  onSelectPipeline?: () => void;
  onSelectStep: (step: ExecutionStep, index: number) => void;
  onExecuteStep?: (stepIndex: number) => void;
  runningStepIndex?: number | null;
};

export const PipelineHorizontalStepper: React.FC<Props> = ({
  pipelineName = 'Pipeline',
  steps = [],
  selectedStep,
  selectedView,
  onSelectPipeline,
  onSelectStep,
  runningStepIndex,
}) => {
  const activeStepRef = React.useRef<HTMLDivElement>(null);
  const [expandedSupervisorIndex, setExpandedSupervisorIndex] = useState<number | null>(null);

  const getEntityIcon = (type?: EntityType, executionMode?: string) => {
    if (executionMode === 'swarm_supervisor') {
      return <HubIcon sx={{ fontSize: 14, color: '#9333ea' }} />;
    }
    switch (type) {
      case EntityType.dataSourceInstance:
        return <StorageIcon sx={{ fontSize: 14 }} />;
      case EntityType.agentInstance:
        return <SmartToyIcon sx={{ fontSize: 14 }} />;
      case EntityType.userAppInstance:
        return <AppsIcon sx={{ fontSize: 14 }} />;
      default:
        return <ExtensionIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getStatusBadge = (step: ExecutionStep, index: number) => {
    if (step.status === 'awaiting_approval') {
      return (
        <Tooltip title="Awaiting Human Approval">
          <WarningAmberIcon
            sx={{
              fontSize: 14,
              color: '#f59e0b',
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(0.95)', opacity: 0.8 },
                '50%': { transform: 'scale(1.15)', opacity: 1 },
                '100%': { transform: 'scale(0.95)', opacity: 0.8 },
              },
            }}
          />
        </Tooltip>
      );
    }
    if (runningStepIndex === index || step.status === 'running') {
      return <CircularProgress size={12} color="primary" thickness={5} />;
    }
    switch (step.status) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 14, color: '#16a34a' }} />;
      case 'failed':
        return <ErrorIcon sx={{ fontSize: 14, color: '#dc2626' }} />;
      default:
        return <RadioButtonUncheckedIcon sx={{ fontSize: 13, color: '#94a3b8' }} />;
    }
  };

  const getSubagentStatusIcon = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 11, color: '#16a34a' }} />;
      case 'running':
        return <CircularProgress size={10} color="secondary" thickness={5} />;
      case 'failed':
        return <ErrorIcon sx={{ fontSize: 11, color: '#dc2626' }} />;
      default:
        return <RadioButtonUncheckedIcon sx={{ fontSize: 10, color: '#94a3b8' }} />;
    }
  };

  const isAnyCompleted = steps.some((s) => s.status === 'completed');
  const isPipelineSelected = selectedView === 'pipeline';

  React.useEffect(() => {
    if (activeStepRef.current) {
      activeStepRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [selectedStep?.id, selectedStep?.index, selectedView, steps.length]);

  const expandedStep = expandedSupervisorIndex !== null ? steps[expandedSupervisorIndex] : null;
  const subagents: SubagentInfo[] = expandedStep?.subagents || [];

  return (
    <Box sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#ffffff', flexShrink: 0 }}>
      {/* Main Horizontal Stepper Row */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          overflowX: 'auto',
          overflowY: 'hidden',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          minHeight: 44,
          height: 48,
          boxSizing: 'border-box',
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 2 },
        }}
      >
        {/* Root Pipeline Node */}
        {onSelectPipeline && (
          <>
            <Tooltip title={`Pipeline: ${pipelineName}`}>
              <Card
                ref={isPipelineSelected ? activeStepRef : undefined}
                variant="outlined"
                sx={{
                  flexShrink: 0,
                  minWidth: 100,
                  maxWidth: 140,
                  cursor: 'pointer',
                  borderRadius: 1.5,
                  transition: 'all 0.15s ease-in-out',
                  border: isPipelineSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                  bgcolor: isPipelineSelected ? '#eff6ff' : '#ffffff',
                  '&:hover': {
                    bgcolor: isPipelineSelected ? '#eff6ff' : '#f8fafc',
                    borderColor: isPipelineSelected ? '#2563eb' : '#94a3b8',
                  },
                }}
              >
                <CardActionArea onClick={onSelectPipeline} sx={{ px: 1, py: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Box sx={{ color: isPipelineSelected ? '#2563eb' : '#64748b', display: 'flex' }}>
                        <AccountTreeIcon sx={{ fontSize: 14 }} />
                      </Box>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          fontWeight: isPipelineSelected ? 700 : 600,
                          fontSize: '0.75rem',
                          color: isPipelineSelected ? '#1e3a8a' : '#1e293b',
                        }}
                      >
                        {pipelineName}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardActionArea>
              </Card>
            </Tooltip>

            {steps.length > 0 && (
              <ChevronRightIcon
                sx={{
                  color: isAnyCompleted ? '#22c55e' : '#cbd5e1',
                  fontSize: 16,
                  flexShrink: 0,
                }}
              />
            )}
          </>
        )}

        {/* Steps Nodes */}
        {steps.map((step, index) => {
          const isSelected = !isPipelineSelected && selectedStep !== undefined && (selectedStep?.id === step.id || selectedStep?.index === index);
          const isCompleted = step.status === 'completed';
          const isFailed = step.status === 'failed';
          const isRunning = step.status === 'running' || runningStepIndex === index;
          const isSupervisor = step.executionMode === 'swarm_supervisor' || (step.subagents && step.subagents.length > 0);
          const subagentCount = step.subagentsCount ?? (step.subagents ? step.subagents.length : 0);
          const isExpanded = expandedSupervisorIndex === index;
          const stepName = step.entityName || step.entityDefName || step.entityDefId || `Step ${index + 1}`;

          return (
            <React.Fragment key={step.index ?? index}>
              <Tooltip title={`Step ${index + 1}: ${stepName} (${step.status || 'pending'})${isSupervisor ? ` • Swarm Supervisor (${subagentCount} subagents)` : ''}`}>
                <Card
                  ref={isSelected ? activeStepRef : undefined}
                  variant="outlined"
                  sx={{
                    flexShrink: 0,
                    minWidth: isSupervisor ? 140 : 110,
                    maxWidth: isSupervisor ? 200 : 160,
                    cursor: 'pointer',
                    borderRadius: 1.5,
                    transition: 'all 0.15s ease-in-out',
                    border: isSelected
                      ? '2px solid #2563eb'
                      : isSupervisor
                        ? '1.5px solid #c084fc'
                        : isRunning
                          ? '2px solid #38bdf8'
                          : isFailed
                            ? '1px solid #f87171'
                            : isCompleted
                              ? '1px solid #86efac'
                              : '1px solid #e2e8f0',
                    bgcolor: isSelected
                      ? '#eff6ff'
                      : isSupervisor
                        ? '#faf5ff'
                        : isRunning
                          ? '#f0f9ff'
                          : '#ffffff',
                    '&:hover': {
                      bgcolor: isSelected ? '#eff6ff' : isSupervisor ? '#f3e8ff' : '#f8fafc',
                      borderColor: isSelected ? '#2563eb' : isSupervisor ? '#9333ea' : '#94a3b8',
                    },
                  }}
                >
                  <CardActionArea onClick={() => onSelectStep(step, index)} sx={{ px: 1, py: 0.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            color: isSelected ? '#2563eb' : isSupervisor ? '#9333ea' : '#64748b',
                          }}
                        >
                          {index + 1}.
                        </Typography>
                        <Box sx={{ color: isSelected ? '#2563eb' : isSupervisor ? '#9333ea' : '#64748b', display: 'flex' }}>
                          {getEntityIcon(step.entityType, step.executionMode)}
                        </Box>
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            fontWeight: isSelected ? 700 : 500,
                            fontSize: '0.75rem',
                            color: isSelected ? '#1e3a8a' : isSupervisor ? '#581c87' : '#1e293b',
                          }}
                        >
                          {stepName}
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {isSupervisor && subagentCount > 0 && (
                          <Chip
                            size="small"
                            label={`${subagentCount} swarm`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedSupervisorIndex(isExpanded ? null : index);
                            }}
                            sx={{
                              height: 16,
                              fontSize: '9px',
                              fontWeight: 700,
                              bgcolor: isExpanded ? '#9333ea' : '#ede9fe',
                              color: isExpanded ? '#ffffff' : '#6b21a8',
                              cursor: 'pointer',
                              '& .MuiChip-label': { px: 0.5 },
                              '&:hover': {
                                bgcolor: '#9333ea',
                                color: '#ffffff',
                              },
                            }}
                          />
                        )}
                        <Box sx={{ display: 'flex' }}>{getStatusBadge(step, index)}</Box>
                        {isSupervisor && subagentCount > 0 && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedSupervisorIndex(isExpanded ? null : index);
                            }}
                            sx={{ p: 0.1, color: '#9333ea' }}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  </CardActionArea>
                </Card>
              </Tooltip>

              {index < steps.length - 1 && (
                <ChevronRightIcon
                  sx={{
                    color: isCompleted ? '#22c55e' : '#cbd5e1',
                    fontSize: 16,
                    flexShrink: 0,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Expandable Child Sub-Stepper Row for Swarm Supervisors */}
      <Collapse in={expandedSupervisorIndex !== null && subagents.length > 0} timeout="auto" unmountOnExit>
        <Box
          sx={{
            px: 2,
            py: 0.5,
            bgcolor: '#faf5ff',
            borderTop: '1px dashed #e9d5ff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflowX: 'auto',
            minHeight: 32,
            '&::-webkit-scrollbar': { height: 3 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#d8b4fe', borderRadius: 2 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#7e22ce', flexShrink: 0 }}>
            <HubIcon sx={{ fontSize: 12 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.04em' }}>
              DELEGATED SWARM ({subagents.length}):
            </Typography>
          </Stack>

          {subagents.map((sub, sIdx) => (
            <React.Fragment key={sub.name || sIdx}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: '6px',
                  bgcolor: '#ffffff',
                  border: '1px solid #d8b4fe',
                  flexShrink: 0,
                }}
              >
                <PsychologyIcon sx={{ fontSize: 11, color: '#9333ea' }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#4c1d95' }}>
                  {sub.name}
                </Typography>
                {sub.role && (
                  <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#6b7280' }}>
                    ({sub.role})
                  </Typography>
                )}
                {getSubagentStatusIcon(sub.status)}
                {sub.duration && (
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#9ca3af' }}>
                    {sub.duration}
                  </Typography>
                )}
              </Box>
              {sIdx < subagents.length - 1 && (
                <Typography variant="caption" sx={{ color: '#c084fc', fontSize: '0.65rem' }}>
                  •
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PipelineHorizontalStepper;
